import { ATTRIBUTE_TYPE, REVIEW_TYPE } from "@apps/reviews/constants";
import AttributesEntity from "@apps/reviews/entities/AttributesEntity";
import ReviewsStorefrontValuesSettingsEntity from "@apps/reviews/entities/ReviewsStorefrontValuesSettingsEntity";
import { AttributesDateValue } from "@apps/reviews/types";
import CacheManager from "@lib/CacheManager/CacheManager";
import { FormManagerValues } from "@lib/FormManager/types";
import { BehaviorSubject, Observable, Subscription } from "rxjs";

import {
    LeaveReviewAttribute,
    LeaveReviewRequestBody,
    UploadImage,
} from "../services/LeaveReviewService";

type NextButtonType = {
    type: "next" | "skip" | "submit" | "close" | "continue";
    isActive: boolean;
    onClick: () => void;
};

type PrevButtonType = {
    type: "prev";
    isActive: boolean;
    onClick: () => void;
};

type ReviewsWizardManagerConstructorParams = {
    attributes$: Observable<AttributesEntity>;
    reviewsSettings$: Observable<ReviewsStorefrontValuesSettingsEntity | null>;
    reviewType: REVIEW_TYPE | undefined;
    onSubmitCollectedData: (data: LeaveReviewRequestBody) => void;
};

class ReviewsWizardManager {
    formData: FormManagerValues = {};
    nextButton$ = new BehaviorSubject<NextButtonType | undefined>(undefined);
    prevButton$ = new BehaviorSubject<PrevButtonType | undefined>(undefined);
    attributes: AttributesEntity | undefined;
    reviewsSettings: ReviewsStorefrontValuesSettingsEntity | undefined;
    reviewType: REVIEW_TYPE | undefined;
    cacheManager: CacheManager;

    attributesSubscription: Subscription;
    reviewsSettingsSubscription: Subscription;

    constructor(
        private readonly params: ReviewsWizardManagerConstructorParams
    ) {
        this.attributesSubscription = params.attributes$.subscribe(
            (attributes) => {
                this.attributes = attributes;
            }
        );

        this.reviewsSettingsSubscription = params.reviewsSettings$.subscribe(
            (settings) => {
                this.reviewsSettings = settings || undefined;
            }
        );

        this.reviewType = this.params.reviewType;

        this.cacheManager = new CacheManager();
    }

    collectFormData(values: FormManagerValues) {
        this.formData = { ...this.formData, ...values };
    }

    submitCollectedFormData() {
        if (!this.formData || !this.attributes || !this.reviewsSettings) {
            return;
        }

        const attributesData: LeaveReviewAttribute = {
            customerAttributes: [],
            productAttributes: [],
        };

        if (this.reviewType === REVIEW_TYPE.PRODUCT_REVIEW && this.attributes) {
            // customerAttributes
            attributesData.customerAttributes =
                this.attributes.customerAttributes
                    .filter((q) => {
                        const value = this.formData[q.id] as
                            | string
                            | string[]
                            | number
                            | AttributesDateValue;

                        if (q.content.type === ATTRIBUTE_TYPE.DATE) {
                            const dateValue = value as AttributesDateValue;
                            // Check (day, month, year)
                            return (
                                (dateValue?.day !== null &&
                                    dateValue?.day !== undefined) ||
                                (dateValue?.month !== null &&
                                    dateValue?.month !== undefined) ||
                                (dateValue?.year !== null &&
                                    dateValue?.year !== undefined)
                            );
                        }

                        return (
                            value !== null &&
                            value !== undefined &&
                            value !== "" &&
                            (!Array.isArray(value) || value.length > 0)
                        );
                    })
                    .map((q) => {
                        let answerContent;

                        switch (q.content.type) {
                            case ATTRIBUTE_TYPE.TEXTFIELD:
                            case ATTRIBUTE_TYPE.SINGLE_VALUE:
                            case ATTRIBUTE_TYPE.LOCATION:
                                answerContent = {
                                    type: q.content.type,
                                    value: this.formData[q.id] as string,
                                };
                                break;
                            case ATTRIBUTE_TYPE.MULTI_VALUE:
                                answerContent = {
                                    type: q.content.type,
                                    values: this.formData[q.id] as string[],
                                };
                                break;
                            case ATTRIBUTE_TYPE.DATE: {
                                const dateValue = this.formData[
                                    q.id
                                ] as AttributesDateValue;
                                answerContent = {
                                    type: q.content.type,
                                    ...(dateValue.day !== null && {
                                        day: dateValue.day,
                                    }),
                                    ...(dateValue.month !== null && {
                                        month: dateValue.month,
                                    }),
                                    ...(dateValue.year !== null && {
                                        year: dateValue.year,
                                    }),
                                };
                                break;
                            }
                            default:
                                answerContent = q.content;
                        }

                        return {
                            commonQuestionId: q.commonId,
                            content: answerContent,
                        };
                    });

            // productAttributes
            attributesData.productAttributes = this.attributes.productAttributes
                .filter((q) => {
                    const value = this.formData[q.id] as
                        | string
                        | string[]
                        | number;
                    return (
                        value !== null &&
                        value !== undefined &&
                        value !== "" &&
                        (!Array.isArray(value) || value.length > 0)
                    );
                })
                .map((q) => {
                    let answerContent;

                    switch (q.content.type) {
                        case ATTRIBUTE_TYPE.TEXTFIELD:
                        case ATTRIBUTE_TYPE.SINGLE_VALUE:
                            answerContent = {
                                type: q.content.type,
                                value: this.formData[q.id] as string,
                            };
                            break;
                        case ATTRIBUTE_TYPE.MULTI_VALUE:
                            answerContent = {
                                type: q.content.type,
                                values: this.formData[q.id] as string[],
                            };
                            break;
                        case ATTRIBUTE_TYPE.RANGE:
                        case ATTRIBUTE_TYPE.CENTERED_RANGE:
                            answerContent = {
                                type: q.content.type,
                                value: this.formData[q.id] as number,
                            };
                            break;
                        default:
                            answerContent = q.content;
                    }

                    return {
                        commonQuestionId: q.commonId,
                        content: answerContent,
                    };
                });
        }

        const data: LeaveReviewRequestBody = {
            body: this.formData.body as string,
            rating: this.formData.rating as number,
            images: this.formData.images as UploadImage[],
            title: (this.formData.title as string) ?? "",
            guestEmail: (this.formData.guestEmail as string) || undefined,
            guestName: (this.formData.guestName as string) || undefined,
            customerDisplayNamePolicy:
                (this.formData.customerDisplayNamePolicy as string) ||
                undefined,
        };

        if (this.reviewType === REVIEW_TYPE.PRODUCT_REVIEW) {
            data.attributes = attributesData;
        }

        this.params.onSubmitCollectedData(data);
    }

    setNextButton(nextButton: NextButtonType | undefined) {
        this.nextButton$.next(nextButton);
    }

    setPrevButton(prevButton: PrevButtonType | undefined) {
        this.prevButton$.next(prevButton);
    }

    setReviewType(reviewType: REVIEW_TYPE | undefined) {
        this.reviewType = reviewType;
    }

    resetForms() {
        this.cacheManager.clear();
        this.formData = {};
    }

    destroy() {
        this.attributesSubscription &&
            this.attributesSubscription.unsubscribe();

        this.reviewsSettingsSubscription &&
            this.reviewsSettingsSubscription.unsubscribe();

        this.resetForms();
    }
}

export default ReviewsWizardManager;
